import pathNode from 'path';
import {
  GameTableType,
  PortalVerticalGameTilePropTypes,
  PortalVerticalTableGameType,
  Sash,
  Table,
} from '../../../types/gameTypes';
import {
  BAD_GAME,
  PORTAL_VERTICAL_GAME_TYPES,
  PORTAL_VERTICAL_SASH_BACKGROUNDS,
  IMAGE_SIZES,
} from '../constants';
import { GameFromAlgolia, SashFromAlgolia } from '../types';
import { createLaunchUrl } from './launchUrl';
import {
  SMALL_BREAKPOINT,
  MEDIUM_BREAKPOINT,
  LARGE_BREAKPOINT,
} from '../../../components/modular-grid/constants';
import config from '../../../config';

const mapSashBackground = (sash: string): string =>
  PORTAL_VERTICAL_SASH_BACKGROUNDS[sash.toLowerCase()] ||
  PORTAL_VERTICAL_SASH_BACKGROUNDS['default'];

export const mapPlayersInfo = ({
  seats,
  players = 0,
}: {
  seats?: number;
  players?: number;
}) => (seats ? `${players} / ${seats}` : `${players}`);

export function convertToObject<T>(result: T[], keys: string[]) {
  return keys.reduce(
    (acc: object, item: string, index: number) => ({
      ...acc,
      [item]: result[index],
    }),
    {}
  );
}

export function filterCloseLiveTableGames(
  games: PortalVerticalGameTilePropTypes[] = []
) {
  return games.filter(
    game => !(game.table_id && game.table?.online.open === false)
  );
}

export const prepareImages = (
  gameImage: PortalVerticalGameTilePropTypes['image']
) => {
  const smallTile = IMAGE_SIZES.small.map(config => ({
    min: config.min,
    max: config.max,
    src: transformImage(gameImage.source.base, config.size),
  }));

  const largeTile = IMAGE_SIZES.large.map(config => ({
    min: config.min,
    max: config.max,
    src: transformImage(gameImage.source.base, config.size),
  }));

  return {
    smallTile,
    largeTile,
  };
};

export const mapTableType = (table?: Table): GameTableType | undefined =>
  table && table.type ? PORTAL_VERTICAL_GAME_TYPES[table.type] : undefined;

export const mapTableStake = (table?: Table): string => {
  const stake = table && table.stake;

  return stake
    ? `${stake.text || 'Stake'} ${stake.currencySymbol}${stake.min}`
    : '';
};

export const mapTable = (
  table?: Table
): PortalVerticalTableGameType | undefined => {
  return (
    table && {
      ...table,
      type: mapTableType(table),
      playersInfo: mapPlayersInfo(table),
      stake: mapTableStake(table),
    }
  );
};

export const toFirstUpper = (text: string): string =>
  text[0].toUpperCase() + text.slice(1, text.length);

const isGameInvalid = (game: GameFromAlgolia) =>
  !game || game.tags.some(tag => BAD_GAME.indexOf(tag) >= 0);

export const filterInvalidGames = (games: GameFromAlgolia[]) =>
  games.filter((game: GameFromAlgolia) => !isGameInvalid(game));

export const reduceArrayToObj = (array: { id: string }[]) =>
  array.reduce((acc, elem) => ({ ...acc, [elem.id]: elem }), {});

const getImgObj = (image: string, width: number, webpFormat?: boolean) => ({
  normal: `${image}?quality=65&width=${width}${
    webpFormat ? `&format=webply` : ''
  }`,
  retina: `${image}?quality=65&width=${width * 1.5}${
    webpFormat ? `&format=webply` : ''
  }`,
});

export const transformImage = (image: string = '', width: number = 200) => {
  const supportedFormats = /.jpg|.png/i;
  const isWebpAvailable = !!image.match(supportedFormats);

  return isWebpAvailable
    ? {
        ...getImgObj(image, width),
        modern: {
          ...getImgObj(image, width, isWebpAvailable),
        },
      }
    : getImgObj(image, width);
};

export function appendTableToGame(
  game: PortalVerticalGameTilePropTypes,
  table?: Table
) {
  return {
    ...game,
    table: table || undefined,
    image: game.image || table?.dealer?.thumbnail || '',
    tableFetching: false,
  };
}

export function mapImage(
  gameImage?: { url: string; title: string; description?: string },
  table?: Table
) {
  const image = gameImage?.url || table?.dealer?.thumbnail;
  const alt = gameImage?.description || gameImage?.title || '';
  return image
    ? { alt, source: transformImage(image) }
    : { source: { normal: '' }, alt };
}

function mapBannerImage(bannerImage?: {
  url: string;
  title: string;
  description?: string;
  content_type: string;
}) {
  if (!bannerImage) return undefined;
  const image = bannerImage.url;
  const alt = bannerImage.description || bannerImage.title || '';

  return {
    alt,
    type: bannerImage.content_type.split('/')[0] as 'image',
    source: {
      normal: image,
    },
  };
}

function mapGameInfoImages(info?: GameFromAlgolia['info_page']) {
  if (
    !info ||
    !info.image_set[0] ||
    !info.image_set[0].large_background ||
    !info.image_set[0].small_medium_background
  )
    return undefined;

  return {
    logo: {
      source: transformImage(info.image_set[0].logo.url, 300),
      alt: info.image_set[0].logo.description || info.image_set[0].logo.title,
    },
    background: [
      {
        max: SMALL_BREAKPOINT.max,
        src: transformImage(
          info.image_set[0].small_medium_background.url,
          SMALL_BREAKPOINT.max - 50
        ),
      },
      {
        min: MEDIUM_BREAKPOINT.min,
        max: MEDIUM_BREAKPOINT.max,
        src: transformImage(info.image_set[0].small_medium_background.url, 375),
      },
      {
        min: LARGE_BREAKPOINT.min,
        src: transformImage(info.image_set[0].large_background.url, 480),
      },
    ],
  };
}

function mapGameImagesToBeSized(
  gameImage?: { url: string; title: string; description?: string },
  table?: Table
) {
  const image = gameImage?.url || table?.dealer?.thumbnail;
  return image
    ? {
        smallTile: [
          {
            max: 500,
            src: transformImage(image, 90),
          },
          {
            max: SMALL_BREAKPOINT.max,
            src: transformImage(image, 160),
          },
          {
            min: MEDIUM_BREAKPOINT.min,
            max: MEDIUM_BREAKPOINT.max,
            src: transformImage(image, 110),
          },
          {
            min: LARGE_BREAKPOINT.min,
            src: transformImage(image, 160),
          },
        ],
        largeTile: [
          {
            max: 500,
            src: transformImage(image, 140),
          },
          {
            max: SMALL_BREAKPOINT.max,
            src: transformImage(image, 270),
          },
          {
            min: MEDIUM_BREAKPOINT.min,
            max: MEDIUM_BREAKPOINT.max,
            src: transformImage(image, 280),
          },
          {
            min: LARGE_BREAKPOINT.min,
            src: transformImage(image, 330),
          },
        ],
      }
    : undefined;
}

function mapSash(sash?: string): Sash | undefined {
  return sash
    ? {
        title: toFirstUpper(sash),
        background: mapSashBackground(sash),
      }
    : undefined;
}

function getSash(
  sash?: string | undefined,
  sashRef?: SashFromAlgolia[] | undefined
): Sash | undefined {
  return sashRef?.[0] ? sashRef[0] : mapSash(sash);
}

export function mapAlgoliaGame({
  game,
  table,
  locale,
  base_path = '',
  game_launch_placement,
}: {
  game: GameFromAlgolia;
  table?: Table;
  locale?: string;
  base_path?: string;
  game_launch_placement?: string;
}): PortalVerticalGameTilePropTypes {
  const jackpotVelocity = Number(game.jackpot_velocity);
  const mappedGame = {
    id: game.uid,
    table_id: game.table_id,
    origin: game.origin,
    sizedImages: mapGameImagesToBeSized(game.image, table),
    presentation_code: game.presentation_code,
    transaction_code: game.transaction_code,
    name: game.title,
    title: game.title.replace(/ - duplicate \d$/, ''),
    info_title: game.info_title,
    type: game.type_global?.game_type,
    description: game.description,
    provider: game.provider,
    integration: game.launch_configuration.integration,
    features: game.features,
    jackpotCode: game.jackpot_code,
    ...(!isNaN(jackpotVelocity) && { jackpotVelocity }),
    // TODO -> consider default image in case when game has image set to null
    image: mapImage(game.image, table),
    bannerImage: mapBannerImage(game.game_banner?.image),
    infoPageImages: mapGameInfoImages(game.info_page),
    launchUrl: createLaunchUrl(game, locale, game_launch_placement),
    rules: game.rules,
    sash: getSash(game.sash, game.sash_ref),
    table: table,
    tableFetching: game.table_id && game.table_id.length > 0 ? true : false,
    //TODO: consider moving path constants to global page properties instead of hardcoding
    gameDetailsPath: pathNode.join(
      base_path,
      `${config.functionalities.gameDetails.path}/${game.presentation_code}`
    ),
    gameLaunchPath: pathNode.join(
      base_path,
      `${config.functionalities.gameLaunch.path}/${game.presentation_code}`
    ),
    seo: game.seo && {
      title: game.seo.seo_title,
      description: game.seo.seo_description,
    },
    tracking_metadata: {
      title: game.title,
    },
    notIndexed: true,
  };

  return mappedGame;
}
